import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Clock,
  Hexagon,
  Layers,
  Info,
  Sparkles,
  TrendingUp,
  FileText,
  Monitor,
  RotateCw,
  Users,
  Network,
  Building2,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
} from "lucide-react";
import "./Features.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Footer from "../../components/Footer/Footer";

const Features = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Features | TeamGrid - AI Workforce Intelligence & Productivity</title>
        <meta name="description" content="Explore TeamGrid features: AI-powered insights, automatic time tracking, productivity scoring, team management, and more. Built for privacy-first workforce intelligence." />
        <link rel="canonical" href="https://teamgrid.ai/features" />
      </Helmet>
      <div className="features-container">
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          {/* Light theme background */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
            }}
          ></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Main Content */}
            <section className="py-16">
              <div>
                {/* Nine Feature Cards Grid */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="mb-20"
                >
                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      {
                        icon: Sparkles,
                        iconColor: "text-[#00D1C1]",
                        iconBg: "bg-[#00D1C1]/10",
                        title: "AI-Powered Insights",
                        desc: "Automatic analysis of work patterns and recommendations",
                        path: "/features/ask-teamgrid",
                      },
                      {
                        icon: Clock,
                        iconColor: "text-[#00D1C1]",
                        iconBg: "bg-[#00D1C1]/10",
                        title: "Automatic Time Tracking",
                        desc: "Silent, precise tracking across all apps and projects",
                        path: "/features/automatic-time-tracking",
                      },
                      {
                        icon: TrendingUp,
                        iconColor: "text-[#00D1C1]",
                        iconBg: "bg-[#00D1C1]/10",
                        title: "Productivity Scoring",
                        desc: "Real-time productivity metrics for individuals and teams",
                        path: "/features/productivity-scoring",
                      },
                      {
                        icon: FileText,
                        iconColor: "text-[#00D1C1]",
                        iconBg: "bg-[#00D1C1]/10",
                        title: "AI Work Summaries",
                        desc: "Auto-generated daily and monthly work reports",
                        path: "/features/ai-work-summaries",
                      },
                      {
                        icon: Monitor,
                        iconColor: "text-[#00D1C1]",
                        iconBg: "bg-[#00D1C1]/10",
                        title: "App & Website Tracking",
                        desc: "Monitor application and website usage patterns",
                        path: "/features/app-website-tracking",
                      },
                      {
                        icon: RotateCw,
                        iconColor: "text-[#00D1C1]",
                        iconBg: "bg-[#00D1C1]/10",
                        title: "Hourly Breakdown",
                        desc: "Detailed hour-by-hour activity analysis",
                        path: "/features/hourly-breakdown",
                      },
                      {
                        icon: Users,
                        iconColor: "text-[#00D1C1]",
                        iconBg: "bg-[#00D1C1]/10",
                        title: "Team Management",
                        desc: "Roles, permissions, and organizational hierarchy",
                        path: "/features/team-management",
                      },
                      {
                        icon: Network,
                        iconColor: "text-[#00D1C1]",
                        iconBg: "bg-[#00D1C1]/10",
                        title: "Project Tracking",
                        desc: "Monitor project progress and team allocations",
                        path: "/features/project-tracking",
                      },
                      {
                        icon: Building2,
                        iconColor: "text-[#00D1C1]",
                        iconBg: "bg-[#00D1C1]/10",
                        title: "Org Intelligence",
                        desc: "AI-driven organization-wide performance insights",
                        path: "/features/org-intelligence",
                      },
                    ].map((feature, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 + idx * 0.05 }}
                      >
                        <div
                          onClick={() =>
                            feature.path && navigate(feature.path)
                          }
                          className={`relative rounded-2xl p-6 border border-gray-200 bg-white features-card-light hover:border-[#00D1C1]/40 hover:shadow-lg transition-all duration-300 ${
                            feature.path
                              ? "cursor-pointer hover:scale-[1.02]"
                              : ""
                          }`}
                        >
                          <div
                            className={`w-12 h-12 rounded-lg ${feature.iconBg} flex items-center justify-center mb-4`}
                          >
                            <feature.icon
                              className={`w-6 h-6 ${feature.iconColor}`}
                            />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-3">
                            {feature.title}
                          </h3>
                          <p className="text-gray-600 text-[15px] leading-relaxed">
                            {feature.desc}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>

            <Footer />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Features;
